import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { FormControl, FormControlLabel, FormGroup } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { Typography, Checkbox } from "@mui/material";
import TextInput from "../../../../components/Utils/TextInput";
import { useAtom, useSetAtom } from "jotai";
import {
	selectedClassAtom,
	combiningClassesArrayAtom,
	unsavedChangesAtom,
} from "../../../../components/Atoms/atoms";
import { suggestRow, buildAssignmentContext } from "./blockAssignment";

export default function ClassAdjustCombineDialog(props) {
	const {
		unwantedCombination,
		setHasCombinedCourse,
		open,
		setOpen,
		rows,
		setRows,
		blockIdsByDept,
	} = props;
	const [validChoices, setValidChoices] = useState(false);
	const [rolesPicked, setRolesPicked] = useState([]);
	const [chosenCTitle, setChosenCTitle] = useState(null);
	const [suggestedCtitle, setSuggestedCTitle] = useState(null);
	const [commonInstructors, setCommonInstructors] = useState([]);
	const [hasEditedCtitle, setHasEditedCTitle] = useState(false);

	const [combiningClassesArray, setCombiningClassesArray] = useAtom(combiningClassesArrayAtom);
	const setUnsavedChanges = useSetAtom(unsavedChangesAtom);

	const [lecCoursesCheck, setLecCoursesCheck] = useState([]);
	const [otherCoursesCheck, setOtherCoursesCheck] = useState([]);
	const [selectedClass, setSelectedClass] = useAtom(selectedClassAtom);

	useEffect(() => {
		if ((!hasEditedCtitle || chosenCTitle === null) && suggestedCtitle?.length > 0) {
			setChosenCTitle(suggestedCtitle);
		}
	}, [suggestedCtitle, hasEditedCtitle, chosenCTitle]);

	useEffect(() => {
		if (commonInstructors.length === 1) {
			setRolesPicked([
				commonInstructors[0].name
					? commonInstructors[0].name
					: commonInstructors[0].user.last_name.concat(", ", commonInstructors[0].user.first_name),
			]);
		}
	}, [commonInstructors]);

	useEffect(() => {
		if (combiningClassesArray && combiningClassesArray.length > 1) {
			const commonNames = combiningClassesArray[0].roles.filter((role) => {
				return combiningClassesArray.every((instructor) => {
					return instructor.roles.some((role2) => {
						const firstUser =
							role.name === null || role.name === undefined
								? role.user.last_name.concat(", ", role.user.first_name)
								: role.name;
						const secondUser =
							role2.name === null || role2.name === undefined
								? role2.user.last_name.concat(", ", role2.user.first_name)
								: role2.name;

						return firstUser === secondUser;
					});
				});
			});
			const commonNames2 = commonNames.map((role) => {
				if (role.name === null || role.name === undefined) {
					return {
						...role,
						checked: false,
						name: role.user.last_name.concat(", ", role.user.first_name),
					};
				}
				return { ...role, checked: false };
			});

			setCommonInstructors(commonNames2);
		}
	}, [combiningClassesArray]);

	useEffect(() => {
		if (rolesPicked?.length > 0 && chosenCTitle) {
			setValidChoices(true);
		} else {
			setValidChoices(false);
		}
	}, [rolesPicked, chosenCTitle]);

	const combinationInfo = useMemo(() => {
		if (!combiningClassesArray || combiningClassesArray.length <= 1 || !selectedClass) {
			return null;
		}

		let subject_id = selectedClass.subject_id;
		let cnumber = selectedClass.cnumber;
		let ctypeForCtitle = selectedClass.ctype;
		let section = selectedClass.section;
		let ctype = "";

		const sortedClasses = [...combiningClassesArray].sort((a, b) => a.cnumber - b.cnumber);

		if (sortedClasses.some((c) => c.ctype === "LEC")) {
			ctype = "LEC";
		} else if (
			sortedClasses.some((c) => c.ctype === "LAB") &&
			!sortedClasses.some((c) => c.ctype === "LEC")
		) {
			ctype = "LAB";
		} else {
			ctype = selectedClass.ctype;
		}

		const sameSubjectId = sortedClasses.every((c) => c.subject_id === subject_id);
		const sameCnumber = sortedClasses.every((c) => c.cnumber === cnumber);
		const sameCtype = sortedClasses.every((c) => c.ctype === ctype);
		const sameSection = sortedClasses.every((c) => c.section === section);

		if (!sameSubjectId) {
			subject_id = [...new Set(sortedClasses.map((c) => c.subject_id))].join("/");
		}
		if (!sameCnumber) {
			cnumber = [...new Set(sortedClasses.map((c) => c.cnumber))].join("/");
		}
		if (!sameCtype) {
			ctypeForCtitle = [...new Set(sortedClasses.map((c) => c.ctype))].join("/");
		}
		if (!sameSection) {
			section = [...new Set(sortedClasses.map((c) => c.section))].join("/");
		}

		const lecCourses = sortedClasses.filter((c) => c.ctype === "LEC");
		const otherCourses = sortedClasses.filter((c) => c.ctype !== "LEC");

		const newCtitle = `${subject_id} ${cnumber} ${ctypeForCtitle} ${section}`;
		setSuggestedCTitle(newCtitle);
		const newCsize = sortedClasses.reduce((sum, c) => sum + c.csize, 0);

		let comb_classes = [];
		combiningClassesArray.forEach((c) => {
			if (c.combine && c.combine.length > 0) {
				comb_classes.push(c.combine);
			}
			comb_classes.push(c.classid);
		});

		comb_classes = comb_classes.filter((c) => c !== selectedClass.classid);
		const comb_class_string = comb_classes.join(",");

		let newId = 0;
		if (sortedClasses.length > 1) {
			sortedClasses.forEach((c) => {
				newId += c.classid;
			});
		}

		return {
			subject_id,
			cnumber,
			ctype,
			section,
			ctypeForCtitle,
			newCtitle,
			newCsize,
			comb_class_string,
			newId,
			lecCourses,
			otherCourses,
		};
	}, [combiningClassesArray, selectedClass]);

	const { roles } = useMemo(() => {
		const roles = [];
		if (!selectedClass) return { roles };

		// Normalize: rolesPicked should always be an array of strings
		const pickedArray = Array.isArray(rolesPicked) ? rolesPicked : rolesPicked ? [rolesPicked] : [];

		pickedArray.forEach((roleName) => {
			const instructor = selectedClass.roles.find((i) =>
				i.name
					? i.name === roleName
					: i.user.last_name.concat(", ", i.user.first_name) === roleName,
			);
			if (instructor) roles.push(instructor);
		});

		return { roles };
	}, [rolesPicked, selectedClass]);

	const newClass = useMemo(() => {
		if (!combinationInfo || !selectedClass) return null;

		const { subject_id, cnumber, ctype, section, newCsize, comb_class_string, newId } =
			combinationInfo;

		return {
			subject_id,
			cnumber,
			ctype,
			section,

			acyear: selectedClass.acyear,
			c_edate: selectedClass.c_edate,
			c_sdate: selectedClass.c_sdate,
			catalog: selectedClass.catalog,
			classid: selectedClass.classid,
			combined: true,
			combine: comb_class_string,
			description: selectedClass.description,
			c_location: selectedClass.c_location,
			csize: newCsize,
			ctitle: chosenCTitle,
			faculty: selectedClass.faculty,
			dept: selectedClass.dept,
			examdate: selectedClass.examdate,
			id: newId,
			name: rolesPicked,
			original: selectedClass.original,
			program: selectedClass.program,
			roles,
			schedule: selectedClass.schedule,
			teamsize: roles.length,
			survey_start: selectedClass.survey_start,
			survey_end: selectedClass.survey_end,
			evaluate: selectedClass.evaluate ? selectedClass.evaluate : selectedClass.exclude_class,
			term: selectedClass.term,
		};
	}, [combinationInfo, selectedClass, chosenCTitle, rolesPicked, roles]);

	const handleClose = (event, reason) => {
		if (reason && reason === "backdropClick") return;
		setOpen(false);
		setRolesPicked(null);
		setChosenCTitle(null);
		setCommonInstructors((prev) => {
			const updatedInstructors = [...prev];
			updatedInstructors.forEach((instructor) => {
				instructor.checked = false;
			});
			return updatedInstructors;
		});
	};

	const onChange = (event) => {
		const { value, checked } = event.target;

		// Update the checked state on the instructor objects
		setCommonInstructors((prev) =>
			prev.map((instr) => (instr.name === value ? { ...instr, checked } : instr)),
		);

		// Update the list of picked roles (names)

		setRolesPicked((prev) => {
			if (checked) {
				// add if not already there
				if (prev?.includes(value)) return prev;
				else if (!prev) return [value];
				return [...prev, value];
			} else {
				// remove if there
				return prev.filter((name) => name !== value);
			}
		});
	};

	const handleSuggestion = () => {
		let suggestedTest = suggestedCtitle;
		setChosenCTitle(suggestedTest);
	};

	const textOnChange = (event) => {
		setHasEditedCTitle(true);
		setChosenCTitle(event.target.value);
	};

	const handleSubmit = () => {
		const ctx = buildAssignmentContext([newClass], blockIdsByDept);
		const suggestedRow = suggestRow(newClass, ctx);

		const updatedClasses = [...rows];
		//find the class in the list of chosen classes
		const classIndex = updatedClasses.findIndex((c) => c.classid === suggestedRow.classid);
		//console.log("classIndex", classIndex);
		//if the class is already in the list, update it
		if (classIndex !== -1) {
			updatedClasses[classIndex] = suggestedRow;
		}

		setSelectedClass(suggestedRow);

		setUnsavedChanges(true);

		setRows(updatedClasses);

		setRolesPicked([]);
		setChosenCTitle(null);
		setCommonInstructors((prev) => {
			const updatedInstructors = [...prev];
			if (updatedInstructors.length > 0) {
				updatedInstructors.forEach((instructor) => {
					instructor.checked = false;
				});
			}

			return updatedInstructors;
		});
		setCombiningClassesArray([]);
		setHasEditedCTitle(false);
		setHasCombinedCourse(true);
		setOpen(false);
	};

	return (
		<div>
			{commonInstructors?.length > 1 && combiningClassesArray?.length > 1 && (
				<Dialog
					open={open}
					onClose={handleClose}
					/* PaperProps={{ variant: "outlined" }} */
					maxWidth="xl"
				>
					<DialogTitle textAlign={"center"}>{"Choose Instructors"}</DialogTitle>

					<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
						<DialogContentText sx={{ marginBottom: "20px" }}>
							These instructors are common across all selected courses.
						</DialogContentText>
						<DialogContentText sx={{ marginBottom: "20px" }}>
							WARNING: It seems like the selected classes might not be suitable for combining.
							Please do not combine LAB or SEM components with a LEC component that has the same
							students enrolled.
						</DialogContentText>

						<FormControl component="fieldset">
							<FormGroup>
								{commonInstructors.map((role) => {
									return (
										<FormControlLabel
											key={role.empid}
											control={
												<Checkbox
													key={role.empid}
													disabled={role.disabled}
													checked={role.checked}
													onChange={onChange}
													name={
														role.name
															? role.name
															: role.user.last_name.concat(", ", role.user.first_name)
													}
													value={
														role.name
															? role.name
															: role.user.last_name.concat(", ", role.user.first_name) || ""
													}
												/>
											}
											label={
												role.name
													? role.name
													: role.user.last_name.concat(", ", role.user.first_name) +
														" (" +
														role.role_description +
														")"
											}
										/>
									);
								})}
							</FormGroup>
						</FormControl>

						{rolesPicked && rolesPicked.length > 0 && (
							<Typography fontSize={18} textAlign={"center"}>
								Set Combined Class Title
							</Typography>
						)}
						{rolesPicked && rolesPicked.length > 0 && (
							<TextInput
								label="Combined Class Title"
								value={chosenCTitle || ""}
								onChange={textOnChange}
							/>
						)}
					</DialogContent>
					<DialogActions>
						<Button color="error" onClick={handleClose}>
							Cancel
						</Button>

						<Button onClick={handleSubmit} autoFocus disabled={!validChoices}>
							Submit
						</Button>
					</DialogActions>
				</Dialog>
			)}
			{commonInstructors?.length === 1 && combiningClassesArray?.length > 1 && (
				<Dialog open={open} onClose={handleClose} maxWidth="xl">
					<DialogTitle textAlign={"center"}>{"Configure Combined Course"}</DialogTitle>

					<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
						<DialogContentText sx={{ mx: "auto" }}>
							The below instructor is assigned to all selected courses. If an instructor is missing,
							please go to the "Edit Classes" page to assign them, then return here.
						</DialogContentText>
						{unwantedCombination && (
							<>
								<DialogContentText sx={{ maxWidth: "600px", color: "red" }}>
									WARNING: It seems like the selected classes might not be suitable for combining.
									Please do not combine LAB or SEM components with a LEC component that has the same
									students enrolled. Combining these sections causes issues with duplicate
									enrollments in the survey platform.
								</DialogContentText>
								<DialogContentText sx={{ maxWidth: "600px", color: "red" }}>
									Example: COURSE 201 LEC 100 and COURSE 201 LAB 100 should not be combined if the
									same students are enrolled in both and the same instructor teaches both
									components.
								</DialogContentText>
								<DialogContentText sx={{ maxWidth: "600px", color: "red" }}>
									Courses in Question:{" "}
								</DialogContentText>
								<DialogContentText sx={{ maxWidth: "600px", color: "red" }}>
									{lecCoursesCheck.length > 0 &&
										`${lecCoursesCheck[0].ctitle} - ${lecCoursesCheck[0].csize} students`}
								</DialogContentText>
								{otherCoursesCheck.length > 0 &&
									otherCoursesCheck.map((c) => {
										return (
											<DialogContentText
												key={c.classid}
												sx={{
													maxWidth: "400px",
													color: "red",
												}}
											>
												{`${c.ctitle} - ${c.csize} students`}
											</DialogContentText>
										);
									})}
								<DialogContentText sx={{ maxWidth: "600px", color: "primary.main" }}>
									If you would still like to evaluate both sections, please submit them on separate
									requisitions (preferably with different questions).
								</DialogContentText>
							</>
						)}

						<div className=" pl-2 font-semibold bg-gray-200 rounded text-primary">
							{commonInstructors[0].name + " - " + commonInstructors[0].class_role}
						</div>

						{rolesPicked && (
							<div>
								<Typography fontSize={18} textAlign={"center"}>
									Set Combined Class Title
								</Typography>
								<TextInput
									label="Combined Class Title"
									value={chosenCTitle || ""}
									onChange={textOnChange}
								/>
							</div>
						)}
					</DialogContent>
					<DialogActions>
						<Button color="error" onClick={handleClose}>
							Cancel
						</Button>

						<Button onClick={handleSubmit} autoFocus disabled={!validChoices}>
							Submit
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</div>
	);
}
